import { Logger } from '@util/logger';

/**
 * Coordinates music playback across multiple browser tabs.
 * Ensures only one tab plays music at a time using BroadcastChannel API.
 */

const CHANNEL_NAME = 'bw-music-tab';
const CLAIM_TIMEOUT = 150; // Time to wait for challenge response (ms)

type Message =
  | { type: 'claim'; tabId: string; force: boolean }
  | { type: 'challenge'; tabId: string }
  | { type: 'release'; tabId: string }
  | { type: 'query'; tabId: string }
  | { type: 'alive'; tabId: string };

interface CoordinatorState {
  currentTabId: string;
  channel: BroadcastChannel | null;
  isMusicTab: boolean;
  claimTimeout: ReturnType<typeof setTimeout> | null;
  musicTabChangeCallbacks: Array<(isMusicTab: boolean) => void>;
  messageHandler: ((event: MessageEvent<Message>) => void) | null;
  beforeUnloadHandler: (() => void) | null;
}

const state: CoordinatorState = {
  currentTabId: generateTabId(),
  channel: null,
  isMusicTab: false,
  claimTimeout: null,
  musicTabChangeCallbacks: [],
  messageHandler: null,
  beforeUnloadHandler: null,
};

/**
 * Generates a unique ID for the current tab.
 */
function generateTabId(): string {
  const RANDOM_SUFFIX_LENGTH = 7;
  return `${Date.now()}-${Math.random()
    .toString(36)
    .slice(2, 2 + RANDOM_SUFFIX_LENGTH)}`;
}

/**
 * Broadcasts a message to all other tabs.
 */
function broadcast(message: Message): void {
  if (state.channel) {
    // BroadcastChannel.postMessage doesn't need targetOrigin (that's for Window.postMessage)
    // eslint-disable-next-line unicorn/require-post-message-target-origin
    state.channel.postMessage(message);
  }
}

/**
 * Notifies all registered callbacks about music tab status change.
 * Also dispatches a custom event for same-tab UI updates.
 */
function notifyMusicTabChange(isMusicTabNow: boolean): void {
  state.isMusicTab = isMusicTabNow;

  for (const callback of state.musicTabChangeCallbacks) {
    callback(isMusicTabNow);
  }

  // Dispatch custom event for same-tab UI updates (e.g., useIsMusicTab hook)
  const event = new CustomEvent('musictabchange', {
    detail: { isMusicTab: isMusicTabNow },
  });
  globalThis.dispatchEvent(event);
}

/**
 * Cancels any pending claim timeout.
 */
function cancelClaimTimeout(): void {
  if (state.claimTimeout !== null) {
    clearTimeout(state.claimTimeout);
    state.claimTimeout = null;
  }
}

/**
 * Checks if the current tab is the music tab.
 */
export function isMusicTab(): boolean {
  return state.isMusicTab;
}

/**
 * Attempts to claim the music tab role for the current tab.
 *
 * @param force When true, this tab will take over as the music tab even if
 * another tab currently holds it. Intended for explicit user actions.
 * @returns true if the claim was initiated successfully, false if channel not initialized
 */
export function claimMusicTab(force: boolean = false): boolean {
  if (!state.channel) {
    Logger.error('Cannot claim music tab: BroadcastChannel not initialized');
    return false;
  }

  // Broadcast claim message to all other tabs
  broadcast({ type: 'claim', tabId: state.currentTabId, force });

  // Immediately become music tab
  // If another tab challenges, message handler will handle the conflict
  if (!state.isMusicTab) {
    notifyMusicTabChange(true);
  }

  return true;
}

/**
 * Releases the music tab role for the current tab.
 */
export function releaseMusicTab(): void {
  if (state.isMusicTab) {
    broadcast({ type: 'release', tabId: state.currentTabId });
    notifyMusicTabChange(false);
  }
}

/**
 * Handles incoming messages from other tabs.
 */
function handleMessage(event: MessageEvent<Message>): void {
  const message = event.data;

  switch (message.type) {
    case 'claim': {
      // Another tab wants to claim music tab
      if (state.isMusicTab) {
        if (message.force) {
          // Forced claim - yield immediately
          notifyMusicTabChange(false);
        } else {
          // Non-forced claim - challenge it
          broadcast({ type: 'challenge', tabId: state.currentTabId });
        }
      }
      break;
    }

    case 'challenge': {
      // Another tab challenged our claim
      // Yield if we're not the one who sent this (which we can't be, BC doesn't echo)
      if (state.isMusicTab) {
        // We were challenged, yield to the challenger
        notifyMusicTabChange(false);
      }
      break;
    }

    case 'release': {
      // Another tab released music tab
      // If we don't have it and no one else claims it quickly, we could claim it
      // For now, do nothing - tabs will claim when they want to play music
      break;
    }

    case 'query': {
      // Another tab is asking who has music tab
      if (state.isMusicTab) {
        broadcast({ type: 'alive', tabId: state.currentTabId });
      }
      break;
    }

    case 'alive': {
      // Another tab has music tab
      if (state.isMusicTab && message.tabId !== state.currentTabId) {
        // Someone else claims to have it, yield to avoid conflict
        notifyMusicTabChange(false);
      }
      break;
    }
  }
}

/**
 * Creates a minimal cleanup function for failed initialization.
 */
function createMinimalCleanup(callback: (isMusicTab: boolean) => void): () => void {
  return (): void => {
    state.musicTabChangeCallbacks = state.musicTabChangeCallbacks.filter((cb) => cb !== callback);
  };
}

/**
 * Initializes the music tab coordinator.
 * Should be called once when the app starts.
 *
 * @returns A cleanup function to stop the coordinator and remove listeners.
 */
export function initializeMusicTabCoordinator(onMusicTabChange: (isMusicTab: boolean) => void): () => void {
  // Register the callback
  state.musicTabChangeCallbacks.push(onMusicTabChange);

  // Create BroadcastChannel if this is the first registration
  if (state.channel) {
    // Channel already exists, notify callback of current state
    onMusicTabChange(state.isMusicTab);
  } else {
    try {
      state.channel = new BroadcastChannel(CHANNEL_NAME);

      // Set up message handler
      state.messageHandler = handleMessage;
      state.channel.addEventListener('message', state.messageHandler);

      // Set up beforeunload handler to release on tab close
      state.beforeUnloadHandler = (): void => {
        releaseMusicTab();
      };
      globalThis.addEventListener('beforeunload', state.beforeUnloadHandler);

      // Query to see if any other tab has music tab
      broadcast({ type: 'query', tabId: state.currentTabId });

      // Wait briefly for response
      setTimeout(() => {
        // If no one responded (we're still not music tab), claim it
        if (!state.isMusicTab) {
          claimMusicTab();
        }
      }, CLAIM_TIMEOUT);
    } catch (error) {
      Logger.error('Failed to initialize BroadcastChannel:', error);
      // Fall back to not coordinating (this tab won't play music)
      onMusicTabChange(false);
      return createMinimalCleanup(onMusicTabChange);
    }
  }

  // Return cleanup function
  return (): void => {
    // Remove this specific callback
    state.musicTabChangeCallbacks = state.musicTabChangeCallbacks.filter((cb) => cb !== onMusicTabChange);

    // If no more callbacks, clean up everything
    if (state.musicTabChangeCallbacks.length === 0) {
      // Release music tab if we have it
      if (state.isMusicTab) {
        releaseMusicTab();
      }

      // Close channel
      if (state.channel) {
        state.channel.close();
        state.channel = null;
        state.messageHandler = null;
      }

      // Remove beforeunload listener
      if (state.beforeUnloadHandler) {
        globalThis.removeEventListener('beforeunload', state.beforeUnloadHandler);
        state.beforeUnloadHandler = null;
      }

      // Cancel any pending timeouts
      cancelClaimTimeout();

      // Reset state
      state.isMusicTab = false;
    }
  };
}

/**
 * Returns the current tab ID.
 */
export function getCurrentTabId(): string {
  return state.currentTabId;
}

/**
 * Resets all module-level state. FOR TESTING PURPOSES ONLY.
 * This should only be called in test cleanup to ensure test isolation.
 */
export function __resetMusicTabCoordinator(): void {
  // Cancel any pending timeouts
  cancelClaimTimeout();

  // Close channel
  if (state.channel) {
    state.channel.close();
    state.channel = null;
  }

  // Remove event listeners
  if (state.beforeUnloadHandler) {
    globalThis.removeEventListener('beforeunload', state.beforeUnloadHandler);
    state.beforeUnloadHandler = null;
  }

  // Reset all state
  state.currentTabId = generateTabId();
  state.isMusicTab = false;
  state.musicTabChangeCallbacks = [];
  state.messageHandler = null;
}
